// src/utils/Math.js
// 基于 Decimal.js 的高精度数学计算工具类
// 使用方式：import Math from '@/utils/mathUtils'; Math.add(0.1, 0.2)
import Decimal from 'decimal.js';
// 统一精度与"常规四舍五入"配置
Decimal.set({
  precision: 40,
  rounding: Decimal.ROUND_HALF_UP,
});

/**
 * 将值转换为 Decimal 对象
 * @param {number|string} v - 输入值
 * @returns {Decimal} Decimal 对象
 */
const d = (v) => new Decimal(String(v || 0));

const MathUtil = {

  /**
   * 格式化结果为字符串
   * @param {Decimal} result - Decimal 计算结果
   * @param {number} [precision] - 保留小数位数
   * @returns {string} 格式化后的字符串
   */
  formatResult(result, precision) {
    if (precision === undefined || precision === null) {
      return result.toString();
    }
    return result.toDecimalPlaces(precision, Decimal.ROUND_HALF_UP).toFixed(precision);
  },

  /**
   * 加法运算
   * @param {number|string} a - 第一个数
   * @param {number|string} b - 第二个数
   * @param {number} [precision] - 保留小数位数（可选）
   * @returns {string} 计算结果
   * @example
   * Math.add(0.1, 0.2) // '0.3'
   * Math.add(0.1, 0.2, 2) // '0.30'
   */
  add(a, b, precision) {
    const result = d(a).plus(d(b));
    return this.formatResult(result, precision);
  },

  /**
   * 减法运算
   * @param {number|string} a - 被减数
   * @param {number|string} b - 减数
   * @param {number} [precision] - 保留小数位数（可选）
   * @returns {string} 计算结果
   * @example
   * Math.sub(0.3, 0.1) // '0.2'
   * Math.sub(1, 0.1, 2) // '0.90'
   */
  sub(a, b, precision) {
    const result = d(a).minus(d(b));
    return this.formatResult(result, precision);
  },

  /**
   * 乘法运算
   * @param {number|string} a - 第一个数
   * @param {number|string} b - 第二个数
   * @param {number} [precision] - 保留小数位数（可选）
   * @returns {string} 计算结果
   * @example
   * Math.mul(0.1, 0.2) // '0.02'
   * Math.mul(2.5, 4, 2) // '10.00'
   */
  mul(a, b, precision) {
    const result = d(a).times(d(b));
    return this.formatResult(result, precision);
  },

  /**
   * 除法运算
   * @param {number|string} a - 被除数
   * @param {number|string} b - 除数
   * @param {number} [precision] - 保留小数位数（可选，默认不限制）
   * @returns {string} 计算结果
   * @example
   * Math.div(0.3, 0.1) // '3'
   * Math.div(1, 3, 4) // '0.3333'
   */
  div(a, b, precision) {
    const result = d(a).div(d(b));
    return this.formatResult(result, precision);
  },

  /**
   * 将值格式化为指定精度的字符串
   * @param {number|string} v - 输入值
   * @param {number} precision - 保留小数位数
   * @returns {string} 格式化后的字符串
   * @example
   * Math.toFixed(1.2345, 2) // '1.23'
   * Math.toFixed(1.235, 2) // '1.24'
   */
  toFixed(v, precision) {
    return d(v).toDecimalPlaces(precision, Decimal.ROUND_HALF_UP).toFixed(precision);
  },

  /**
   * 将数字格式化为货币格式（千分位分隔）
   * @param {number|string} v - 输入值
   * @param {number} [precision=2] - 保留小数位数，默认为2
   * @returns {string} 格式化后的货币字符串
   * @example
   * Math.mathMoneyFixed(1000) // '1,000.00'
   * Math.mathMoneyFixed(1234567.89) // '1,234,567.89'
   * Math.mathMoneyFixed(1000, 0) // '1,000'
   */
  mathMoneyFixed(v, precision = 2) {
    const num = d(v);
    const fixed = num.toDecimalPlaces(precision, Decimal.ROUND_HALF_UP).toFixed(precision);
    const parts = fixed.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  },

  /**
   * 将值格式化为指定精度的数字
   * @param {number|string} v - 输入值
   * @param {number} precision - 保留小数位数
   * @returns {number} 格式化后的数字
   * @example
   * Math.toNumber(1.2345, 2) // 1.23
   * Math.toNumber(1.235, 2) // 1.24
   */
  toNumber(v, precision) {
    return Number(this.toFixed(v, precision));
  },

  /**
   * 列表求和
   * @param {Array} list - 数据列表
   * @param {string} [field] - 求和字段（可选，不传则直接对列表项求和）
   * @param {number} [precision] - 保留小数位数（可选）
   * @returns {string} 求和结果
   * @example
   * Math.sum([0.1, 0.2]) // '0.3'
   * Math.sum([{price: 0.1}, {price: 0.2}], 'price', 2) // '0.30'
   */
  sum(list, field, precision) {
    if (!Array.isArray(list) || list.length === 0) {
      return this.formatResult(d(0), precision);
    }
    const result = list.reduce((acc, item) => {
      const val = field !== undefined ? item?.[field] : item;
      return acc.plus(d(val || 0));
    }, d(0));
    return this.formatResult(result, precision);
  },

  /**
   * 比较两个数的大小
   * @param {number|string} a - 第一个数
   * @param {number|string} b - 第二个数
   * @returns {number} -1: a<b, 0: a=b, 1: a>b
   * @example
   * Math.compare(0.1, 0.2) // -1
   * Math.compare(0.1, 0.1) // 0
   * Math.compare(0.2, 0.1) // 1
   */
  compare(a, b) {
    return d(a).comparedTo(d(b));
  },

  /**
   * 取两个数中的较大值
   * @param {number|string} a - 第一个数
   * @param {number|string} b - 第二个数
   * @returns {string} 较大值
   * @example
   * Math.max(0.1, 0.2) // '0.2'
   */
  max(a, b) {
    return Decimal.max(d(a), d(b)).toString();
  },

  /**
   * 取两个数中的较小值
   * @param {number|string} a - 第一个数
   * @param {number|string} b - 第二个数
   * @returns {string} 较小值
   * @example
   * Math.min(0.1, 0.2) // '0.1'
   */
  min(a, b) {
    return Decimal.min(d(a), d(b)).toString();
  },

  /**
   * 取绝对值
   * @param {number|string} v - 输入值
   * @returns {string} 绝对值
   * @example
   * Math.abs(-1.5) // '1.5'
   */
  abs(v) {
    return d(v).abs().toString();
  },

  /**
   * 四舍五入
   * @param {number|string} v - 输入值
   * @param {number} precision - 保留小数位数
   * @returns {string} 四舍五入后的值
   * @example
   * Math.round(1.235, 2) // '1.24'
   */
  round(v, precision) {
    return d(v).toDecimalPlaces(precision, Decimal.ROUND_HALF_UP).toString();
  },

  /**
   * 向上取整
   * @param {number|string} v - 输入值
   * @returns {string} 向上取整后的值
   * @example
   * Math.ceil(1.2) // '2'
   */
  ceil(v) {
    return d(v).ceil().toString();
  },

  /**
   * 向下取整
   * @param {number|string} v - 输入值
   * @returns {string} 向下取整后的值
   * @example
   * Math.floor(1.9) // '1'
   */
  floor(v) {
    return d(v).floor().toString();
  },

  /**
   * Vue 插件安装方法
   * 挂载 $math 到 Vue 原型，并注册全局过滤器
   * @param {Vue} Vue - Vue 构造函数
   * @example
   * import mathUtils from '@/utils/mathUtils';
   * Vue.use(mathUtils); // 在 main.js 中调用
   */
  install(Vue) {
    // 挂载到 Vue 原型，组件中可通过 this.$math 访问
    Vue.prototype.$math = this;
    
    // 注册全局过滤器
    Vue.filter('mathAdd', (value, num) => this.add(value, num));//使用时：{{ 123 | mathAdd(456) }} 输出 579
    Vue.filter('mathSub', (value, num) => this.sub(value, num));//使用时：{{ 123 | mathSub(456) }} 输出 -333
    Vue.filter('mathMul', (value, num) => this.mul(value, num));//使用时：{{ 123 | mathMul(456) }} 输出 56088
    Vue.filter('mathDiv', (value, num) => this.div(value, num));//使用时：{{ 123 | mathDiv(456) }} 输出 0.271
    Vue.filter('mathFixed', (value, precision = 2) => this.toFixed(value, precision));//使用时：{{ 123.456789 | mathFixed(2) }} 输出 123.46
    Vue.filter('mathMoneyFixed', (value, precision = 2) => this.mathMoneyFixed(value, precision));//使用时：{{ 1234567.89 | mathMoneyFixed(2) }} 输出 1,234,567.89
    Vue.filter('mathSum', (list, field, precision) => this.sum(list, field, precision));//使用时：{{ [{price: 0.1}, {price: 0.2}] | mathSum('price', 2) }} 输出 0.30
    Vue.filter('mathAbs', (value) => this.abs(value));//使用时：{{ -123 | mathAbs }} 输出 123
    Vue.filter('mathRound', (value, precision = 2) => this.round(value, precision));//使用时：{{ 123.456789 | mathRound(2) }} 输出 123.46
    Vue.filter('mathCeil', (value) => this.ceil(value));//使用时：{{ 1.1 | mathCeil }} 输出 2
    Vue.filter('mathFloor', (value) => this.floor(value));//使用时：{{ 1.9 | mathFloor }} 输出 1
  }
};

export default MathUtil;
